import {Box, Flex, Heading, Text, Divider, IconButton, Checkbox, useDisclosure, useToast} from "@chakra-ui/react";
import moment from "moment/moment";
import {InputLabel, TextareaLabel} from "../../../../components/Theme";
import React, {useEffect, useState} from "react";
import httpRequest from "../../../../core/services/http-request";
import DeleteShippingDetailModal from "../../../EstimateSu/EstimateModals/deleteShippingDetailModal";
import {ReactComponent as DeleteLum} from "../../../../assets/del.svg";
import InputMask from "react-input-mask";
import styles from "../../../EstimateSu/EstimateTabs/ShippingPages/styles";
import {ReactComponent as SendMessage} from "../../../../assets/unfinishedIcon.svg";
import {useUser} from "../../../../core/hooks";

const parseAirtableProducts = (data) => {
    if (!data) return [];
    let parsed = data;

    if (typeof data === 'string') {
        try {
            parsed = JSON.parse(data);
        } catch (e) {
            return [];
        }
    }

    if (!Array.isArray(parsed)) parsed = [parsed];

    return parsed.map(product => product?.fields || product).filter(Boolean);
};

const expandShippingLuminaires = (shippingLuminaires = []) =>
    shippingLuminaires.flatMap((item, parentIndex) => {
        // Current shipping records already contain one flattened product per row.
        if (item?.description || item?.orderCode || item?.uniqueKey) {
            return [item];
        }

        const fixtureQty = Number(
            item?.recomendedFixtureQty ||
            item?.fixtureQty ||
            item?.qty ||
            item?.globalRecomendedFixtureQty ||
            item?.globalQty ||
            1
        );
        const shippedFixtureQty = Number(item?.qtyShippingDetails ?? fixtureQty);
        const shippedRatio = fixtureQty > 0 ? shippedFixtureQty / fixtureQty : 1;
        const categories = [
            {
                type: 'fixture',
                data: item?.fixtureRecomendedAirtableArray || item?.fixtureRecomendedAirtable,
                multiplier: 1,
            },
            {
                type: 'lamp',
                data: item?.lampRecomendedAirtable || item?.lampRecomendedAirtableArray,
                multiplier: Number(item?.recomendedLampQty || item?.lampPerFixture || 1),
            },
            {
                type: 'control',
                data: item?.controlRecomendedAirtableArray || item?.controlRecomendedAirtable,
                multiplier: Number(item?.controlRecomendedQty || item?.controlQTYFixture || 1),
            },
            {
                type: 'powerSupply',
                data: item?.powerSupplyRecomendedAirtableArray || item?.powerSupplyRecomendedAirtable,
                multiplier: Number(item?.powerSupplyRecomendedQty || item?.powerQTYFixture || 1),
            },
            {
                type: 'accessory',
                data: item?.accessoryRecomendedAirtableArray || item?.accessoryRecomendedAirtable,
                multiplier: Number(item?.accessoryRecomendedQty || item?.accessoryDescriptionAQty || 1),
            },
        ];
        const components = [];

        categories.forEach(category => {
            parseAirtableProducts(category.data).forEach((fields, productIndex) => {
                const description = fields?.['FE Product Description'] || fields?.['Product Description'] || fields?.['Description'];
                const orderCode = fields?.['FE Order Code'] || fields?.['Order Code'] || fields?.['Part Number'];

                if (!description && !orderCode) return;

                const totalMaxQty = fixtureQty * category.multiplier;
                components.push({
                    ...item,
                    description: description || 'N/A',
                    orderCode: orderCode || 'N/A',
                    totalMaxQty,
                    qtyShippingDetails: totalMaxQty * shippedRatio,
                    uniqueKey: `${item.id || parentIndex}-${category.type}-${productIndex}`,
                });
            });
        });

        return components.length > 0 ? components : [item];
    });

const ShippingViewDetails = ({estimate, trackId, callback, edit, setEdit, setShippingPageStatus, setLoading, setEditShippingAllow, getEstimate, editShippingAllow}) => {
    const {post} = httpRequest()
    const {user} = useUser()
    const [luminairesForDetails, setLuminairesForDetails] = useState([])
    const [luminaires, setLuminaires] = useState([])
    const [info, setInfo] = useState({})
    const [error, setError] = useState(false)
    const {isOpen, onOpen, onClose} = useDisclosure()
    const toast = useToast()

    const getLuminaireField = (item, fieldKey) => {
        if (fieldKey === 'FE Product Description' && item?.description) {
            return item.description;
        }
        if (fieldKey === 'FE Order Code' && item?.orderCode) {
            return item.orderCode;
        }
        if (item?.lampRecomendedAirtable?.length > 0) {
            try {
                const parsed = JSON.parse(item.lampRecomendedAirtable);
                if (parsed && parsed[fieldKey]) return parsed[fieldKey];
            } catch (e) {
            }
        }
        if (item?.fixtureRecomendedAirtableArray?.length > 0) {
            return item.fixtureRecomendedAirtableArray[0]?.fields?.[fieldKey] || '';
        }
        if (item?.powerSupplyRecomendedAirtableArray?.length > 0) {
            return item.powerSupplyRecomendedAirtableArray[0]?.fields?.[fieldKey] || '';
        }
        if (item?.controlRecomendedAirtableArray?.length > 0) {
            return item.controlRecomendedAirtableArray[0]?.fields?.[fieldKey] || '';
        }
        if (item?.accessoryRecomendedAirtableArray?.length > 0) {
            return item.accessoryRecomendedAirtableArray[0]?.fields?.[fieldKey] || '';
        }
        return '';
    };
    const getMaxQty = (item) => item?.totalMaxQty ??
        item?.recomendedFixtureQty ??
        item?.qty ??
        item?.recomendedLampQty ??
        1;
    useEffect(() => {
        post('get-shipping-details', trackId).then((e) => {
            setLuminairesForDetails(e.data.data)
            setLuminaires(expandShippingLuminaires(e.data.data[0]?.luminaires || []))
            setInfo({
                senderCompanyName: e.data.data[0]?.senderCompanyName,
                senderTrackingLink: e.data.data[0]?.senderTrackingLink,
                senderAddress1: e.data.data[0]?.senderAddress1,
                senderAddress2: e.data.data[0]?.senderAddress2,
                senderCity: e.data.data[0]?.senderCity,
                senderState: e.data.data[0]?.senderState,
                senderZipCode: e.data.data[0]?.senderZipCode,
                senderPhone: e.data.data[0]?.senderPhone,
                receiverCompanyName: e.data.data[0]?.receiverCompanyName,
                receiverAddress1: e.data.data[0]?.receiverAddress1,
                receiverAddress2: e.data.data[0]?.receiverAddress2,
                receiverCity: e.data.data[0]?.receiverCity,
                receiverState: e.data.data[0]?.receiverState,
                receiverZipCode: e.data.data[0]?.receiverZipCode,
                receiverPhone: e.data.data[0]?.receiverPhone,
                receiverName: e.data.data[0]?.receiverName,
            })
        })
    }, [])

    useEffect(() => {
        if (editShippingAllow){
            setLoading(true)
            let data = {
                estimateId: estimate.id,
                trackId: trackId,
                luminaires: luminaires,
                data: info,
            }
            post('edit-shipping-detail', data).then(() => {
                setShippingPageStatus(1)
                getEstimate()
                setLoading(false)
                toast({
                    render: () => (

                        <Flex
                            overflow='hidden'
                            position='relative'
                            justifyContent='center'
                            flexDirection='column'
                            borderRadius='4px 0px 0px 4px'
                            marginTop='80px'
                            color='black'
                            bg='#8FCF74'
                            pl={3}
                            width='530px'
                            fontWeight='600'
                            fontSize='14px'
                            height='40px'>
                            <Flex alignItems='center'>
                                <SendMessage/>
                                <Text
                                    mt={1}
                                    ml={2}>
                                    Shipping Detail Edited!
                                </Text>
                            </Flex>
                            <Box
                                css={styles.toast}
                                position='absolute'
                                left='5px'
                                bottom='0'
                                mt={1}
                                borderRadius='6px'
                                backgroundColor='#D5D5D5'
                                height='3px'
                                width='40px'>
                            </Box>
                        </Flex>
                    ),
                    duration: 4000,
                    isClosable: true,
                    position: "top-right"
                })
            }).catch((e) => {
                setLoading(false)
            })
            setEditShippingAllow(false)
            setEdit(false)
        }
    }, [editShippingAllow])

    return (
        luminairesForDetails?.length > 0 && (
            <>
                <Flex position='relative' justifyContent='space-between'>
                    <DeleteShippingDetailModal
                        getEstimate={getEstimate}
                        setEdit={setEdit}
                        trackId={trackId}
                        setShippingPageStatus={setShippingPageStatus}
                        onClose={onClose}
                        isOpen={isOpen}/>
                    <IconButton
                        display={edit ? 'flex' : 'none'}
                        onClick={onOpen}
                        height="30px"
                        minW="30px"
                        position="absolute"
                        top="-45px"
                        left="-45px"
                        borderRadius="5px"
                        bg="#FB7575"
                        aria-label="del"
                        icon={<DeleteLum width="15px" height="15px"/>}/>
                    <img
                        src="/images/LogoIcon.svg"
                        alt=""/>
                    <Flex
                        flexDirection='column'>
                        <Heading
                            m={0}
                            textAlign='end'>
                            Shipping Detail
                        </Heading>
                        <Text textAlign='end'>
                            Reference #: {estimate?.reference}
                        </Text>
                        <Text
                            textAlign='end'>
                            Date : {moment(estimate?.shippingDateCreated?.date).format('MM/DD/YYYY')}
                        </Text>
                    </Flex>
                </Flex>
                <Flex
                    justifyContent='space-between'>
                    <Flex
                        width='49%'
                        borderRight='2px solid #9C9C9C'
                        flexDirection='column'>
                        <Box
                            backgroundColor='#EEEEEE'
                            width='98%'
                            borderRadius='5px'
                            fontSize='18px'
                            pl={4}
                            pt={2}
                            pb={2}
                            color='#222222'
                            fontWeight='700'>
                            Sender Information:
                        </Box>
                        <InputLabel
                            placeholder='Company Name'
                            readOnly={!edit}
                            onChange={(e) => setInfo({...info, senderCompanyName: e.target.value})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            maxW='320px'
                            defaultValue={user?.type === 'Reu' ? 'Focus Earth' : luminairesForDetails[0]?.senderCompanyName}/>
                        {
                            edit ? (
                                <InputLabel
                                    placeholder='Tracking Link'
                                    onChange={(e) => setInfo({...info, senderTrackingLink: e.target.value})}
                                    border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                    readOnly={!edit}
                                    maxW='320px'
                                    defaultValue={luminairesForDetails[0]?.senderTrackingLink}/>
                            ) : (
                                <Text pl={4} my={5}>
                                    <a href={(luminairesForDetails[0]?.senderTrackingLink && !luminairesForDetails[0]?.senderTrackingLink.startsWith("https://")) ? `https://${luminairesForDetails[0]?.senderTrackingLink}` : luminairesForDetails[0]?.senderTrackingLink} target='_blank' rel="noreferrer">
                                        {luminairesForDetails[0]?.senderTrackingLink}
                                    </a>
                                </Text>
                            )
                        }

                        <InputLabel
                            placeholder='Address Line 1'
                            onChange={(e) => setInfo({...info, senderAddress1: e.target.value})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            readOnly={!edit}
                            maxW='320px'
                            defaultValue={user?.type === 'Reu' ? '1070 Tunnel Road, Building 1, Suite 10, #202' : luminairesForDetails[0]?.senderAddress1}/>
                        <InputLabel
                            placeholder='Address Line 2'
                            onChange={(e) => setInfo({...info, senderAddress2: e.target.value})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            readOnly={!edit}
                            maxW='320px'
                            defaultValue={user?.type === 'Reu' ? '' : luminairesForDetails[0]?.senderAddress2}/>
                        <Flex
                            justifyContent='space-between'
                            width='320px'>
                            <InputLabel
                                placeholder='City'
                                onChange={(e) => setInfo({...info, senderCity: e.target.value})}
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                readOnly={!edit}
                                maxW='150px'
                                defaultValue={user?.type === 'Reu' ? 'Asheville' : luminairesForDetails[0]?.senderCity}/>
                            <InputLabel
                                placeholder='State'
                                onChange={(e) => setInfo({...info, senderState: e.target.value})}
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                readOnly={!edit}
                                maxW='150px'
                                defaultValue={user?.type === 'Reu' ? 'NC': luminairesForDetails[0]?.senderState}/>
                        </Flex>
                        <Flex
                            justifyContent='space-between'
                            width='320px'>
                            <InputLabel
                                placeholder='Zip Code'
                                onChange={(e) => setInfo({...info, senderZipCode: e.target.value})}
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                readOnly={!edit}
                                maxW='150px'
                                defaultValue={user?.type === 'Reu' ? '28805' : luminairesForDetails[0]?.senderZipCode}/>
                            <InputLabel
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                readOnly
                                maxW='150px'
                                value={luminairesForDetails[0]?.senderCountry}/>
                        </Flex>
                        <InputLabel
                            placeholder='Phone'
                            onChange={(e) => setInfo({...info, senderPhone: e.target.value})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            mask="999-999-9999"
                            maskChar=""
                            autoComplete="on"
                            as={InputMask}
                            formatChars={{'9': '[0-9]'}}
                            readOnly={!edit}
                            maxW='320px'
                            defaultValue={user?.type === 'Reu' ? '(925) 323-0053' : luminairesForDetails[0]?.senderPhone}/>
                    </Flex>
                    <Flex
                        width='50%'
                        flexDirection='column'>
                        <Box
                            backgroundColor='#EEEEEE'
                            width='100%'
                            borderRadius='5px'
                            fontSize='18px'
                            pl={4}
                            pt={2}
                            pb={2}
                            color='#222222'
                            fontWeight='700'>
                            Recipient Information:
                        </Box>
                        <InputLabel
                            placeholder='Company Name'
                            onChange={(e) => setInfo({...info, receiverCompanyName: e.target.value})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            readOnly={!edit}
                            maxW='320px'
                            defaultValue={luminairesForDetails[0]?.receiverCompanyName}/>
                        <InputLabel
                            placeholder='Receiver Name'
                            onChange={(e) => setInfo({...info, receiverName: e.target.value.slice(7)})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            readOnly={!edit}
                            maxW='320px'
                            defaultValue={`Attn : ${luminairesForDetails[0]?.receiverName}`}/>
                        <InputLabel
                            placeholder='Address Line 1'
                            onChange={(e) => setInfo({...info, receiverAddress1: e.target.value})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            readOnly={!edit}
                            maxW='320px'
                            defaultValue={luminairesForDetails[0]?.receiverAddress1}/>
                        <InputLabel
                            placeholder='Address Line 2'
                            onChange={(e) => setInfo({...info, receiverAddress2: e.target.value})}
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            readOnly={!edit}
                            maxW='320px'
                            defaultValue={luminairesForDetails[0]?.receiverAddress2}/>
                        <Flex
                            justifyContent='space-between'
                            width='320px'>
                            <InputLabel
                                placeholder='City'
                                onChange={(e) => setInfo({...info, receiverCity: e.target.value})}
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                readOnly={!edit}
                                maxW='150px'
                                defaultValue={luminairesForDetails[0]?.receiverCity}/>
                            <InputLabel
                                placeholder='State'
                                onChange={(e) => setInfo({...info, receiverState: e.target.value})}
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                readOnly={!edit}
                                maxW='150px'
                                defaultValue={luminairesForDetails[0]?.receiverState}/>
                        </Flex>
                        <Flex
                            justifyContent='space-between'
                            width='320px'>
                            <InputLabel
                                placeholder='Zip Code'
                                onChange={(e) => setInfo({...info, receiverZipCode: e.target.value})}
                                readOnly={!edit}
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                maxW='150px'
                                defaultValue={luminairesForDetails[0]?.receiverZipCode}/>
                            <InputLabel
                                border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                readOnly
                                maxW='150px'
                                value={luminairesForDetails[0]?.receiverCountry}/>
                        </Flex>
                        <InputLabel
                            placeholder='Phone'
                            border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                            readOnly={!edit}
                            mask="999-999-9999"
                            maskChar=""
                            autoComplete="on"
                            as={InputMask}
                            formatChars={{'9': '[0-9]'}}
                            onChange={(e) => setInfo({...info, receiverPhone: e.target.value})}
                            maxW='320px'
                            defaultValue={luminairesForDetails[0]?.receiverPhone}/>
                    </Flex>
                </Flex>
                <Box
                    backgroundColor='#FF9A7A'
                    width='100%'
                    fontSize='18px'
                    fontWeight='700'
                    pl={4}
                    pt={2}
                    pb={2}
                    borderRadius='5px'
                    mt={10}>
                    Shipping Information
                </Box>
                <Flex
                    mt={7}
                    mb={7}
                    fontWeight='600'>
                    <Text
                        display={edit ? 'block' : 'none'}
                        width='10%'>
                        Select
                    </Text>
                    <Text
                        ml={2}
                        width={edit ? '40%' : '50%'}>
                        Description
                    </Text>
                    <Text
                        width='30%'>
                        Part Number
                    </Text>
                    <Text
                        width='20%'>
                        QTY
                    </Text>
                </Flex>
                {
                    luminaires?.map((item, index) =>
                        <Flex
                            key={item.uniqueKey || `${item.id || 'shipping-item'}-${index}`}
                            mb={4}
                            backgroundColor={edit ? '#EEEEEE' : '#FAFAFA'}
                            border={edit ? '1px solid #A9A9A9' : 'none'}
                            borderRadius='6px'
                            minH='64px'
                            alignItems='center'>
                            <Text
                                display={edit ? 'block' : 'none'}
                                width='10%'>
                                <Checkbox
                                    css={styles.checkbox}
                                    onChange={(e) => {
                                        const updated = [...luminaires]
                                        updated[index].isChecked = e.target.checked
                                        setLuminaires(updated)
                                        callback(updated)
                                    }}
                                    size='lg'
                                    iconColor='black'/>
                            </Text>
                            <Text
                                ml={2}
                                width={edit ? '40%' : '50%'}>
                                {getLuminaireField(item, 'FE Product Description')}
                            </Text>
                            <Text
                                width='30%'>
                                {getLuminaireField(item, 'FE Order Code')}
                            </Text>
                            <Box
                                mb={2}
                                width='20%'>
                                <InputLabel
                                    maxW='50%'
                                    backgroundColor={edit ? 'white !important' : 'transparent !important'}
                                    border={edit ? '1px solid #A9A9A9 !important' : 'none !important'}
                                    readOnly={!edit}
                                    type='number'
                                    isInvalid={error}
                                    onChange={(e) => {
                                        const maxQty = Number(getMaxQty(item))
                                        let qty = e.target.value
                                        if (qty <= maxQty && qty >= 0) {
                                            setError(false)
                                            const updated = [...luminaires]
                                            updated[index].qtyShippingDetails = qty;
                                            setLuminaires(updated)
                                            return callback(updated)
                                        }
                                        setError(true)
                                    }}
                                    defaultValue={item.qtyShippingDetails ?? getMaxQty(item)}/>
                            </Box>
                        </Flex>
                    )
                }
                <TextareaLabel
                    minH='56px'
                    backgroundColor='#EEEEEE !important'
                    disabled
                    value={luminairesForDetails[0]?.notes ?? ''}
                    label='Notes'/>
                <Divider
                    my={10}
                    borderColor="#222222"
                    border="2px solid"
                    orientation="horizontal"/>
            </>
        )
    )
}

export default ShippingViewDetails
